import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { Lock, KeyRound, ShieldCheck } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function ChangePassword() {
  const { user, setUser, logout } = useAuth();
  const nav = useNavigate();
  const forced = !!user?.must_change_password;
  const [current, setCurrent] = useState("");
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  if (!user) return <Navigate to="/login" replace />;

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 8) return toast.error("Password must be at least 8 characters");
    if (pw !== confirm) return toast.error("Passwords do not match");
    if (pw === current) return toast.error("New password must be different from current");
    setBusy(true);
    try {
      await api.post("/users/me/change-password", { current_password: current, new_password: pw });
      setUser({ ...user, must_change_password: false });
      toast.success("Password changed");
      nav("/");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-black text-white grid lg:grid-cols-2">
      <div className="hidden lg:flex relative grid-bg-brand p-12 flex-col justify-between border-r border-[#1c1c1c]">
        <div className="font-heading text-lg font-bold uppercase tracking-tight">Chongie Aviation</div>
        <div className="max-w-md">
          <div className="label-eyebrow mb-3" style={{ color: "#8a83d4" }}>STEP 1 OF 1</div>
          <h1 className="font-heading text-4xl font-bold uppercase tracking-tight leading-tight">
            Set your password.
          </h1>
          <p className="mt-6 text-sm text-[#b9b2e8] max-w-sm">
            For security, you must replace your temporary password with one only you know before continuing.
          </p>
          <ul className="mt-6 space-y-2 text-xs font-mono text-[#b9b2e8]">
            <li className="flex items-center gap-2"><ShieldCheck className="h-3 w-3" /> Minimum 8 characters</li>
            <li className="flex items-center gap-2"><ShieldCheck className="h-3 w-3" /> Different from current</li>
            <li className="flex items-center gap-2"><ShieldCheck className="h-3 w-3" /> Stored as a bcrypt hash</li>
          </ul>
        </div>
        <div className="font-mono text-xs text-[#666]">JWT • bcrypt • audit logged</div>
      </div>

      <div className="relative flex items-center justify-center p-8 lg:p-16 bg-brand-vertical">
        <div className="absolute inset-0 grid-bg opacity-40" />
        <form onSubmit={submit} className="relative w-full max-w-md space-y-6" data-testid="change-password-form">
          <div>
            <div className="label-eyebrow">{forced ? "ACTION REQUIRED" : "ACCOUNT SECURITY"}</div>
            <h2 className="font-heading text-3xl uppercase font-bold mt-2">Change password</h2>
            <p className="text-sm text-[#a1a1aa] mt-2">
              {forced
                ? "Welcome to Chongie Aviation. Set a personal password to continue."
                : "Pick a new password to replace the current one."}
            </p>
          </div>

          <PwField id="current" label="CURRENT PASSWORD" value={current} set={setCurrent} testid="cp-current" />
          <PwField id="new" label="NEW PASSWORD" value={pw} set={setPw} testid="cp-new" />
          <PwField id="confirm" label="CONFIRM NEW PASSWORD" value={confirm} set={setConfirm} testid="cp-confirm" />

          <Button
            type="submit"
            disabled={busy}
            data-testid="cp-submit"
            className="w-full h-12 rounded-none btn-brand border border-[#3b2bd0] font-heading font-bold uppercase tracking-[0.15em]"
          >
            <KeyRound className="h-4 w-4 mr-2" /> {busy ? "Saving…" : "Set new password"}
          </Button>

          {forced && (
            <button
              type="button"
              onClick={async () => { await logout(); nav("/login"); }}
              className="block mx-auto text-xs font-mono text-[#888] hover:text-white underline-offset-4 hover:underline"
            >
              Sign out instead
            </button>
          )}
        </form>
      </div>
    </div>
  );
}

function PwField({ id, label, value, set, testid }) {
  return (
    <div>
      <Label htmlFor={id} className="label-eyebrow">{label}</Label>
      <div className="relative mt-2">
        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#666]" />
        <Input
          id={id}
          type="password"
          value={value}
          onChange={(e) => set(e.target.value)}
          required
          data-testid={testid}
          className="pl-10 bg-black border-[#222] rounded-none h-12 font-mono text-sm"
        />
      </div>
    </div>
  );
}
